<template>
    <div>
        <div class="text-[#1A1916]">

            <!-- LOADER -->
            <div v-if="loading" class="flex flex-col items-center justify-center py-32">
                <div class="flex gap-2 mb-4">
                    <div class="w-2 h-2 bg-orange-500 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                    <div class="w-2 h-2 bg-orange-500 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                    <div class="w-2 h-2 bg-orange-500 rounded-full animate-bounce"></div>
                </div>
                <p class="text-sm text-gray-400">Chargement du catalogue…</p>
            </div>

            <!-- LAYOUT -->
            <div v-else class="flex gap-5 items-start">

                <!-- ══ SIDEBAR ══ -->
                <div class="w-56 flex-shrink-0 bg-white border border-[#E4E2DC] rounded-xl overflow-hidden shadow-sm sticky top-5 flex flex-col">

                    <!-- Header -->
                    <div class="px-4 py-3 border-b border-[#E4E2DC] text-[10.5px] font-semibold tracking-widest uppercase text-[#A8A49D]">
                        Catégories
                    </div>

                    <!-- Liste -->
                    <div class="p-1.5 flex-1">
                        <div
                            v-for="cat in categories"
                            :key="cat.id"
                            class="group flex items-center gap-2 px-2.5 py-2 rounded-lg cursor-pointer transition-all text-xs font-medium select-none"
                            :class="activeCategoryId === cat.id
                                ? 'bg-[#1A1916] text-white'
                                : 'text-[#6B6860] hover:bg-[#FAFAF8] hover:text-[#1A1916]'"
                            @click="selectCategory(cat.id)">

                            <!-- Icône -->
                            <span
                                class="w-7 h-7 rounded-lg flex items-center justify-center text-xs flex-shrink-0 transition-all"
                                :class="activeCategoryId === cat.id ? 'bg-white/15' : cat.color">
                                <i :class="cat.icon || 'fas fa-tag'"></i>
                            </span>

                            <!-- Nom inline-éditable au double-clic -->
                            <span
                                v-if="editingCatId !== cat.id"
                                class="flex-1 truncate"
                                @dblclick.stop="startEditCat(cat)">
                                {{ cat.nom }}
                            </span>
                            <input
                                v-else
                                ref="catInput"
                                v-model="editingCatName"
                                class="flex-1 min-w-0 bg-white/15 border border-white/30 rounded px-1.5 py-0.5 text-xs text-white outline-none"
                                @blur="saveEditCat(cat)"
                                @keydown.enter="saveEditCat(cat)"
                                @keydown.esc="editingCatId = null"
                                @click.stop />

                            <!-- Compteur -->
                            <span
                                class="text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 tabular-nums"
                                :class="activeCategoryId === cat.id ? 'bg-white/15' : 'bg-black/[0.07]'">
                                {{ countForCat(cat.id) }}
                            </span>

                            <!-- Supprimer -->
                            <button
                                class="opacity-0 group-hover:opacity-100 w-5 h-5 flex items-center justify-center rounded text-[10px] flex-shrink-0 transition-all"
                                :class="activeCategoryId === cat.id
                                    ? 'text-white/40 hover:text-white hover:bg-white/10'
                                    : 'text-gray-300 hover:text-red-500 hover:bg-red-50'"
                                @click.stop="deleteCategory(cat)">
                                <i class="fas fa-times"></i>
                            </button>
                        </div>
                    </div>

                    <!-- Footer nouvelle catégorie -->
                    <div class="p-2.5" :class="categories.length ? 'border-t border-[#E4E2DC]' : 'pt-0'">

                        <button
                            v-if="!showNewCat"
                            class="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg border border-dashed border-[#D4D0C8] text-[#A8A49D] text-xs font-medium transition-all hover:border-orange-400 hover:text-orange-500 hover:bg-orange-50"
                            @click="showNewCat = true">
                            <i class="fas fa-plus text-[9px]"></i>
                            Nouvelle catégorie
                        </button>

                        <div v-else class="space-y-2.5">

                            <!-- Nom -->
                            <input
                                ref="newCatInput"
                                v-model="newCatForm.nom"
                                class="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-xs focus:outline-none focus:border-orange-400 bg-[#F5F4F1] placeholder-gray-300"
                                placeholder="Nom de la catégorie…"
                                @keydown.enter="createCategory"
                                @keydown.esc="cancelNewCat" />

                            <!-- Icônes -->
                            <div>
                                <p class="text-[9px] font-bold uppercase tracking-wider text-gray-300 mb-1.5">Icône</p>
                                <div class="grid grid-cols-4 gap-1 max-h-36 overflow-y-auto pr-0.5">
                                    <button
                                        v-for="ico in iconOptions"
                                        :key="ico.value"
                                        class="flex flex-col items-center gap-0.5 p-1.5 rounded-lg border transition-all"
                                        :class="newCatForm.icon === ico.value
                                            ? 'border-orange-400 bg-orange-50 text-orange-500'
                                            : 'border-gray-100 text-gray-400 hover:border-gray-200 hover:text-gray-600'"
                                        :title="ico.label"
                                        @click="newCatForm.icon = ico.value">
                                        <i :class="[ico.value, 'text-xs']"></i>
                                    </button>
                                </div>
                            </div>

                            <!-- Couleurs -->
                            <div>
                                <p class="text-[9px] font-bold uppercase tracking-wider text-gray-300 mb-1.5">Couleur</p>
                                <div class="flex flex-wrap gap-1.5">
                                    <button
                                        v-for="col in colorOptions"
                                        :key="col.value"
                                        class="w-6 h-6 rounded-full border-2 transition-all flex items-center justify-center"
                                        :class="[col.dot, newCatForm.color === col.value ? 'border-[#1A1916] scale-110' : 'border-transparent hover:scale-105']"
                                        @click="newCatForm.color = col.value">
                                        <i v-if="newCatForm.color === col.value" class="fas fa-check text-white" style="font-size:8px"></i>
                                    </button>
                                </div>
                            </div>

                            <!-- Aperçu + actions -->
                            <div class="flex items-center gap-2">
                                <span
                                    class="w-7 h-7 rounded-lg flex items-center justify-center text-xs flex-shrink-0"
                                    :class="newCatForm.color">
                                    <i :class="newCatForm.icon"></i>
                                </span>
                                <span class="flex-1 text-xs font-medium text-gray-600 truncate">
                                    {{ newCatForm.nom || 'Aperçu' }}
                                </span>
                                <button
                                    class="w-7 h-7 rounded-lg bg-[#1A1916] hover:bg-orange-500 text-white flex items-center justify-center transition-all text-xs"
                                    @click="createCategory">
                                    <i class="fas fa-check"></i>
                                </button>
                                <button
                                    class="w-7 h-7 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-400 flex items-center justify-center transition-all text-xs"
                                    @click="cancelNewCat">
                                    <i class="fas fa-times"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- ══ MAIN ══ -->
                <div class="flex-1 min-w-0 flex flex-col gap-3">

                    <!-- Aucune catégorie -->
                    <div v-if="!activeCategoryId" class="bg-white border-2 border-dashed border-[#E4E2DC] rounded-xl py-20 text-center">
                        <div class="text-3xl text-[#C8C4BC] mb-3"><i class="fas fa-layer-group"></i></div>
                        <p class="text-sm text-[#A8A49D] leading-relaxed">Sélectionnez une catégorie pour gérer<br />ses tarifs et prestations</p>
                    </div>

                    <template v-else>

                        <!-- Toolbar -->
                        <div class="flex items-center justify-between gap-3">
                            <div class="flex items-center gap-2.5">
                                <span class="inline-flex items-center gap-2 bg-[#1A1916] text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                                    <span class="w-5 h-5 rounded-md flex items-center justify-center text-[10px]" :class="activeCategoryObj.color">
                                        <i :class="activeCategoryObj.icon"></i>
                                    </span>
                                    {{ activeCategoryObj.nom }}
                                </span>
                                <span class="text-xs text-[#A8A49D] tabular-nums">
                                    {{ filteredLines.length }} ligne{{ filteredLines.length > 1 ? 's' : '' }}
                                </span>
                            </div>
                            <div class="relative">
                                <i class="fas fa-search absolute left-2.5 top-1/2 -translate-y-1/2 text-[10px] text-gray-300 pointer-events-none"></i>
                                <input
                                    v-model="search"
                                    type="text"
                                    class="border border-gray-200 rounded-lg pl-7 pr-3 py-1.5 text-xs focus:outline-none focus:border-orange-400 w-44 placeholder-gray-300"
                                    placeholder="Rechercher…" />
                            </div>
                        </div>

                        <!-- Tableau -->
                        <div class="bg-white border border-[#E4E2DC] rounded-xl overflow-hidden shadow-sm">
                            <div class="overflow-x-auto">
                                <table class="w-full border-collapse text-xs" style="min-width:820px">

                                    <thead>
                                        <tr class="bg-[#1A1916] text-white text-left">
                                            <th class="px-3 py-2.5 text-[10px] font-semibold tracking-wide uppercase w-[28%]">
                                                Libellé / Description
                                            </th>
                                            <th class="px-2 py-2.5 text-[10px] font-semibold tracking-wide uppercase text-center w-[12%]">
                                                Qté / Unité
                                            </th>
                                            <th class="px-3 py-2.5 text-[10px] font-semibold tracking-wide uppercase text-right w-[14%]">
                                                Coût HT
                                                <span class="block text-[9px] font-normal text-white/40 normal-case tracking-normal">Prix d'achat</span>
                                            </th>
                                            <th class="px-2 py-2.5 text-[10px] font-semibold tracking-wide uppercase text-center w-[15%]">
                                                Marge
                                                <span class="block text-[9px] font-normal text-white/40 normal-case tracking-normal">% ou €</span>
                                            </th>
                                            <th class="px-3 py-2.5 text-[10px] font-semibold tracking-wide uppercase text-right w-[14%]">
                                                PU HT
                                                <span class="block text-[9px] font-normal text-white/40 normal-case tracking-normal">Prix de vente</span>
                                            </th>
                                            <th class="px-2 py-2.5 text-[10px] font-semibold tracking-wide uppercase text-center w-[9%]">
                                                TVA
                                            </th>
                                            <th class="w-8"></th>
                                        </tr>
                                    </thead>

                                    <tbody class="divide-y divide-[#F0EFEC]">

                                        <!-- Empty -->
                                        <tr v-if="filteredLines.length === 0">
                                            <td colspan="7" class="py-12 text-center text-gray-300 text-xs">
                                                <div class="text-center">
                                                    <i class="fas fa-inbox text-xl mb-2 text-gray-200"></i>
                                                </div>
                                                <span v-if="!search">Aucune ligne — cliquez sur <strong class="text-gray-400">+ Ajouter</strong> pour commencer</span>
                                                <span v-else>Aucun résultat pour « {{ search }} »</span>
                                            </td>
                                        </tr>

                                        <tr
                                            v-for="line in filteredLines"
                                            :key="line.id"
                                            class="group hover:bg-orange-50/30 transition-colors">

                                            <!-- Libellé + Description -->
                                            <td class="px-2 py-1.5 align-top">
                                                <input
                                                    class="w-full border border-gray-200 rounded px-2 py-1 text-xs font-semibold text-[#1A1916] focus:outline-none focus:border-orange-400 placeholder-gray-200 bg-white"
                                                    :value="line.libelle"
                                                    placeholder="Libellé…"
                                                    @blur="onCellBlur(line, 'libelle', $event)"
                                                    @keydown.enter="$event.target.blur()" />
                                                <input
                                                    class="w-full border border-gray-100 rounded px-2 py-1 mt-0.5 text-[11px] text-gray-400 focus:outline-none focus:border-orange-300 placeholder-gray-200 bg-white"
                                                    :value="line.description"
                                                    placeholder="Description…"
                                                    @blur="onCellBlur(line, 'description', $event)"
                                                    @keydown.enter="$event.target.blur()" />
                                            </td>

                                            <!-- Qté + Unité -->
                                            <td class="px-2 py-1.5 align-top">
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.5"
                                                    class="w-full border border-gray-200 rounded px-2 py-1 text-xs text-center font-semibold focus:outline-none focus:border-orange-400 bg-white"
                                                    :value="line.qtt"
                                                    @blur="onCellBlurNum(line, 'qtt', $event)"
                                                    @keydown.enter="$event.target.blur()" />
                                                <select
                                                    class="w-full border border-gray-100 rounded px-1 py-1 mt-0.5 text-[11px] text-gray-400 focus:outline-none focus:border-orange-300 bg-white cursor-pointer"
                                                    :value="line.unite"
                                                    @change="onCellSelect(line, 'unite', $event)">
                                                    <option value="h">h</option>
                                                    <option value="j">j</option>
                                                    <option value="m²">m²</option>
                                                    <option value="m">m</option>
                                                    <option value="ml">ml</option>
                                                    <option value="u">unité</option>
                                                    <option value="forfait">forfait</option>
                                                    <option value="kg">kg</option>
                                                </select>
                                            </td>

                                            <!-- Coût HT -->
                                            <td class="px-2 py-1.5 align-top">
                                                <div class="relative">
                                                    <input
                                                        type="number" min="0" step="0.01"
                                                        class="w-full border border-gray-200 rounded pl-2 pr-5 py-1 text-xs text-right focus:outline-none focus:border-orange-400 placeholder-gray-200 bg-white"
                                                        :value="line.cout_ht"
                                                        placeholder="0"
                                                        @blur="onCellBlurNum(line, 'cout_ht', $event, true)"
                                                        @keydown.enter="$event.target.blur()" />
                                                    <span class="absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] text-gray-300 pointer-events-none">€</span>
                                                </div>
                                            </td>

                                            <!-- Marge -->
                                            <td class="px-2 py-1.5 align-top">
                                                <div class="relative">
                                                    <input
                                                        type="number" min="0" step="0.1"
                                                        class="w-full border border-gray-200 rounded pl-2 pr-5 py-1 text-xs text-right focus:outline-none focus:border-orange-400 placeholder-gray-200 bg-white"
                                                        :class="line.marge > 0 ? 'text-emerald-600 font-semibold' : 'text-[#1A1916]'"
                                                        :value="line.marge"
                                                        placeholder="0"
                                                        @blur="onMargeBlur(line, $event)"
                                                        @keydown.enter="$event.target.blur()" />
                                                    <span class="absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] text-gray-300 pointer-events-none">{{ line.type_marge === 'eur' ? '€' : '%' }}</span>
                                                </div>
                                                <div class="flex gap-0.5 mt-0.5">
                                                    <button
                                                        class="flex-1 py-0.5 rounded text-[9px] font-bold border transition-all"
                                                        :class="line.type_marge !== 'eur'
                                                            ? 'bg-orange-500 border-orange-500 text-white'
                                                            : 'bg-white border-gray-200 text-gray-300 hover:border-gray-300 hover:text-gray-500'"
                                                        @click="setMargeMode(line, 'pct')">%</button>
                                                    <button
                                                        class="flex-1 py-0.5 rounded text-[9px] font-bold border transition-all"
                                                        :class="line.type_marge === 'eur'
                                                            ? 'bg-orange-500 border-orange-500 text-white'
                                                            : 'bg-white border-gray-200 text-gray-300 hover:border-gray-300 hover:text-gray-500'"
                                                        @click="setMargeMode(line, 'eur')">€</button>
                                                </div>
                                            </td>

                                            <!-- PU HT -->
                                            <td class="px-2 py-1.5 align-top">
                                                <div class="relative">
                                                    <input
                                                        type="number" min="0" step="0.01"
                                                        class="w-full border border-orange-200 rounded pl-2 pr-5 py-1 text-xs text-right font-semibold text-orange-600 focus:outline-none focus:border-orange-400 placeholder-orange-200 bg-orange-50/40"
                                                        :value="line.prix_ht"
                                                        placeholder="0"
                                                        @blur="onPuBlur(line, $event)"
                                                        @keydown.enter="$event.target.blur()" />
                                                    <span class="absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] text-orange-300 pointer-events-none">€</span>
                                                </div>
                                            </td>

                                            <!-- TVA -->
                                            <td class="px-2 py-1.5 align-top">
                                                <select
                                                    class="w-full border border-gray-200 rounded px-1 py-1 text-xs text-center focus:outline-none focus:border-orange-400 bg-white cursor-pointer"
                                                    :value="line.tva"
                                                    @change="onTvaChange(line, $event)">
                                                    <option :value="0">0%</option>
                                                    <option :value="5.5">5,5%</option>
                                                    <option :value="10">10%</option>
                                                    <option :value="20">20%</option>
                                                </select>
                                            </td>

                                            <!-- Supprimer -->
                                            <td class="pr-1.5 text-center align-top py-1.5">
                                                <button
                                                    class="opacity-0 group-hover:opacity-100 w-6 h-6 flex items-center justify-center rounded text-[10px] text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all mx-auto mt-0.5"
                                                    @click="deleteLine(line)">
                                                    <i class="fas fa-trash"></i>
                                                </button>
                                            </td>
                                        </tr>

                                    </tbody>
                                </table>
                            </div>

                            <!-- Bouton ajouter -->
                            <div class="border-t border-[#F0EFEC] px-3 py-2 bg-[#FAFAF8]">
                                <button
                                    class="inline-flex items-center gap-1.5 border border-dashed border-[#D4D0C8] rounded-lg px-3 py-1.5 text-xs text-[#A8A49D] font-medium transition-all hover:border-orange-400 hover:text-orange-500 hover:bg-orange-50 disabled:opacity-40"
                                    :disabled="savingNew"
                                    @click="addLine">
                                    <i class="fas fa-plus text-[9px]"></i>
                                    Ajouter une ligne
                                </button>
                            </div>
                        </div>

                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'CatalogueFacturation',

    data() {
        return {
            loading: true,
            categories: [],
            lines: [],
            activeCategoryId: null,
            search: '',

            editingCatId:   null,
            editingCatName: '',

            showNewCat: false,
            newCatForm: { nom: '', icon: 'fas fa-tag', color: 'bg-orange-100 text-orange-600' },

            savingNew: false,

            iconOptions: [
                // Main d'œuvre & terrain
                { value: 'fas fa-wrench',            label: 'Outillage' },
                { value: 'fas fa-tools',             label: 'Outils' },
                { value: 'fas fa-hard-hat',          label: 'Travaux' },
                { value: 'fas fa-hammer',            label: 'Marteau' },
                { value: 'fas fa-screwdriver',       label: 'Tournevis' },
                { value: 'fas fa-toolbox',           label: 'Boîte à outils' },
                // Énergie & fluides
                { value: 'fas fa-fire-alt',          label: 'Chauffage' },
                { value: 'fas fa-fire',              label: 'Flamme' },
                { value: 'fas fa-bolt',              label: 'Électricité' },
                { value: 'fas fa-tint',              label: 'Plomberie' },
                { value: 'fas fa-snowflake',         label: 'Climatisation' },
                { value: 'fas fa-wind',              label: 'Ventilation' },
                { value: 'fas fa-solar-panel',       label: 'Solaire' },
                { value: 'fas fa-plug',              label: 'Prise / Branchement' },
                { value: 'fas fa-broadcast-tower',   label: 'Réseau' },
                // Équipements & fournitures
                { value: 'fas fa-box',               label: 'Matériaux' },
                { value: 'fas fa-boxes',             label: 'Stock' },
                { value: 'fas fa-cube',              label: 'Pièce' },
                { value: 'fas fa-cogs',              label: 'Mécanique' },
                { value: 'fas fa-pump-soap',         label: 'Pompe' },
                // Services & administratif
                { value: 'fas fa-clipboard-check',   label: 'Services' },
                { value: 'fas fa-clipboard-list',    label: 'Contrat' },
                { value: 'fas fa-file-invoice',      label: 'Facturation' },
                { value: 'fas fa-truck',             label: 'Transport' },
                { value: 'fas fa-shipping-fast',     label: 'Livraison' },
                // Générique
                { value: 'fas fa-tag',               label: 'Général' },
                { value: 'fas fa-star',              label: 'Premium' },
                { value: 'fas fa-layer-group',       label: 'Divers' },
            ],

            colorOptions: [
                { value: 'bg-orange-100 text-orange-600', dot: 'bg-orange-400' },
                { value: 'bg-red-100 text-red-600',       dot: 'bg-red-400' },
                { value: 'bg-yellow-100 text-yellow-600', dot: 'bg-yellow-400' },
                { value: 'bg-green-100 text-green-600',   dot: 'bg-green-400' },
                { value: 'bg-teal-100 text-teal-600',     dot: 'bg-teal-400' },
                { value: 'bg-blue-100 text-blue-600',     dot: 'bg-blue-400' },
                { value: 'bg-indigo-100 text-indigo-600', dot: 'bg-indigo-400' },
                { value: 'bg-purple-100 text-purple-600', dot: 'bg-purple-400' },
                { value: 'bg-pink-100 text-pink-600',     dot: 'bg-pink-400' },
                { value: 'bg-gray-100 text-gray-600',     dot: 'bg-gray-400' },
            ],
        }
    },

    computed: {
        activeCategoryObj() {
            if (!this.activeCategoryId) return null
            return this.categories.find(c => c.id === this.activeCategoryId) || null
        },

        filteredLines() {
            const q = this.search.toLowerCase().trim()
            return this.lines.filter(l => {
                if (l.categorie_id !== String(this.activeCategoryId)) return false
                if (q && !(l.libelle || '').toLowerCase().includes(q) && !(l.description || '').toLowerCase().includes(q)) return false
                return true
            })
        },
    },

    methods: {
        async load() {
            this.loading = true
            try {
                const [cats, lns] = await Promise.all([
                    this.$request.post('catalogue/categories/list', {}),
                    this.$request.post('catalogue/lines/list', {}),
                ])
                this.categories = cats || []
                this.lines = (lns || []).map(l => ({
                    ...l,
                    categorie_id: String(l.categorie_id),
                    type_marge: l.type_marge || 'pct',
                    tva: parseFloat(l.tva) || 0,
                }))
                if (this.categories.length && !this.activeCategoryId)
                    this.activeCategoryId = this.categories[0].id
            } finally {
                this.loading = false
            }
        },

        countForCat(catId) {
            return this.lines.filter(l => l.categorie_id === String(catId)).length
        },

        selectCategory(id) {
            this.activeCategoryId = id
            this.search = ''
        },

        // ── Catégories ───────────────────────────────────────
        async createCategory() {
            const nom = (this.newCatForm.nom || '').trim()
            if (!nom) return
            const res = await this.$request.post('catalogue/categories/create', {
                nom,
                icon:  this.newCatForm.icon,
                color: this.newCatForm.color,
            })
            const created = res.api ?? res
            this.categories.push(created)
            this.activeCategoryId = created.id
            this.cancelNewCat()
            this.$toast.success('Catégorie créée')
            this.$eventHub.$emit('refresh-warnings')
        },

        cancelNewCat() {
            this.showNewCat = false
            this.newCatForm = { nom: '', icon: 'fas fa-tag', color: 'bg-orange-100 text-orange-600' }
        },

        startEditCat(cat) {
            this.editingCatId   = cat.id
            this.editingCatName = cat.nom
            this.$nextTick(() => {
                const inp = this.$refs.catInput
                ;(Array.isArray(inp) ? inp[0] : inp)?.focus()
            })
        },

        async saveEditCat(cat) {
            const nom = (this.editingCatName || '').trim()
            this.editingCatId = null
            if (!nom || nom === cat.nom) return
            cat.nom = nom
            await this.$request.post('catalogue/categories/update', { id: cat.id, nom })
        },

        async deleteCategory(cat) {
            if (!confirm(`Supprimer « ${cat.nom} » et toutes ses lignes ?`)) return
            await this.$request.post('catalogue/categories/delete', { id: cat.id })
            this.categories = this.categories.filter(c => c.id !== cat.id)
            this.lines       = this.lines.filter(l => l.categorie_id !== String(cat.id))
            if (this.activeCategoryId === cat.id)
                this.activeCategoryId = this.categories[0]?.id || null
            this.$toast.success('Catégorie supprimée')
        },

        // ── Lignes ───────────────────────────────────────────
        async addLine() {
            if (!this.activeCategoryId || this.savingNew) return
            this.savingNew = true
            try {
                const res = await this.$request.post('catalogue/lines/create', {
                    categorie_id: this.activeCategoryId,
                    libelle: '', description: '', qtt: 1, unite: 'u',
                    cout_ht: 0, marge: 0, type_marge: 'pct',
                    prix_ht: 0, prix_ttc: 0, tva: 10,
                })
                const newLine = res.api ?? res
                this.lines.push({
                    ...newLine,
                    categorie_id: String(newLine.categorie_id),
                    type_marge: newLine.type_marge || 'pct',
                })
            } finally {
                this.savingNew = false
            }
        },

        async deleteLine(line) {
            if (!confirm(`Supprimer « ${line.libelle || 'cette ligne'} » ?`)) return
            await this.$request.post('catalogue/lines/delete', { id: line.id })
            this.lines = this.lines.filter(l => l.id !== line.id)
            this.$toast.success('Ligne supprimée')
        },

        // ── Cellules ─────────────────────────────────────────
        async updateCell(line, field, value) {
            await this.$request.post('catalogue/lines/update-cell', { id: line.id, field, value })
        },

        onCellBlur(line, field, event) {
            const val = event.target.value
            if (val !== line[field]) {
                line[field] = val
                this.updateCell(line, field, val)
            }
        },

        onCellBlurNum(line, field, event, recalc = false) {
            const val = parseFloat(event.target.value) || 0
            if (val !== line[field]) {
                line[field] = val
                this.updateCell(line, field, val)
                if (recalc) this.recalcFromCout(line)
            }
        },

        onCellSelect(line, field, event) {
            const val = event.target.value
            line[field] = val
            this.updateCell(line, field, val)
        },

        onTvaChange(line, event) {
            line.tva      = parseFloat(event.target.value)
            line.prix_ttc = this.calcTTC(line)
            this.updateCell(line, 'tva',      line.tva)
            this.updateCell(line, 'prix_ttc', line.prix_ttc)
        },

        setMargeMode(line, mode) {
            line.type_marge = mode
            this.recalcFromCout(line)
            this.updateCell(line, 'type_marge', mode)
        },

        onMargeBlur(line, event) {
            const val = parseFloat(event.target.value) || 0
            if (val !== line.marge) {
                line.marge = val
                this.updateCell(line, 'marge', val)
                this.recalcFromCout(line)
            }
        },

        onPuBlur(line, event) {
            const val = parseFloat(event.target.value) || 0
            if (val === line.prix_ht) return
            line.prix_ht = val
            if (line.cout_ht > 0 && val > 0) {
                line.marge = line.type_marge === 'eur'
                    ? Math.round((val - line.cout_ht) * 100) / 100
                    : Math.round(((val - line.cout_ht) / line.cout_ht) * 100 * 10) / 10
            }
            line.prix_ttc = this.calcTTC(line)
            this.updateCell(line, 'prix_ht',  val)
            this.updateCell(line, 'marge',     line.marge)
            this.updateCell(line, 'prix_ttc',  line.prix_ttc)
        },

        recalcFromCout(line) {
            if (line.cout_ht > 0) {
                line.prix_ht = line.type_marge === 'eur'
                    ? Math.round((line.cout_ht + (line.marge || 0)) * 100) / 100
                    : Math.round(line.cout_ht * (1 + (line.marge || 0) / 100) * 100) / 100
            }
            line.prix_ttc = this.calcTTC(line)
            this.updateCell(line, 'prix_ht',  line.prix_ht)
            this.updateCell(line, 'prix_ttc', line.prix_ttc)
        },

        calcTTC(line) {
            const ht  = parseFloat(line.prix_ht) || 0
            const tva = parseFloat(line.tva) || 0
            return Math.round(ht * (1 + tva / 100) * 100) / 100
        },

        fmtPrix(v) {
            return (v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        },
    },

    watch: {
        showNewCat(v) {
            if (v) this.$nextTick(() => this.$refs.newCatInput?.focus())
        },
    },

    mounted() {
        this.load()
    },
}
</script>